import { ReactNode } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import TextInput from '../TextInput';
import combineClasses from 'classnames';
import PrimaryButton from '../PrimaryButton/PrimaryButton';
import { Dialog, DialogContent } from '../Dialog';

interface Props {
  className?: string;
  hideModal: () => void;
  show?: boolean;
  title: string;
  confirmTestString: string;
  instructions: string;
  warningMessage: string | ReactNode;
  performIrreversibleAction: () => void | Promise<void>;
  [k: string]: string | unknown | (() => void) | ReactNode;
}

interface FormValues {
  [key: string]: string;
}

const ConfirmIrreversible = ({
  className,
  hideModal,
  show = true,
  title,
  confirmTestString,
  warningMessage,
  instructions,
  performIrreversibleAction,
  ...buttonProps
}: Props): JSX.Element => {
  const initialValue: FormValues = {
    confirmTest: '',
  };

  const schemaValues = {
    confirmTest: Yup.string()
      .required(`Please type '${confirmTestString}' to proceed`)
      .oneOf([confirmTestString], `Please type '${confirmTestString}' to proceed`),
  };

  const validationSchema = Yup.object().shape(schemaValues);

  const handleConfirm = async () => {
    try {
      await performIrreversibleAction();
    } catch {
      console.error('Could not complete deletion');
    } finally {
      hideModal();
    }
  };

  return (
    <Dialog
      open={show}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          hideModal();
        }
      }}
    >
      <DialogContent
        className='w-[min(95vw,500px)] border-0 bg-transparent p-0 shadow-none max-[468px]:max-h-[100vh] max-[468px]:overflow-y-scroll'
        overlayClassName='bg-black/50'
      >
        <div className={combineClasses('overflow-hidden rounded-[15px] bg-white', className)}>
          <div className='relative flex items-center justify-center bg-[#012a73] px-[1.5rem] py-[1.5rem] text-white'>
            <h2 className="m-0 max-w-[80%] text-center font-['GT-Walsheim-Pro-Regular'] text-[2.1rem] leading-[1.05]">
              {title}
            </h2>
            <button
              type='button'
              onClick={hideModal}
              aria-label='Close confirmation dialog'
              className='absolute right-[10px] top-[10px] cursor-pointer border-0 bg-transparent text-[2.4rem] leading-[2.4rem] text-white'
            >
              x
            </button>
          </div>
          <div className='flex flex-col gap-[3.1em] px-[3.9em] pb-[4.2em] pt-[4.4em] max-[468px]:gap-[1.6em] max-[468px]:p-[2.2em]'>
            <p className="m-0 flex max-w-[25em] min-w-[25em] flex-col font-['GT-Walsheim-Pro-Light'] text-[1.6rem] leading-[1.6] max-[375px]:max-w-full max-[375px]:min-w-0">
              {warningMessage}
            </p>
            <p className="m-0 flex max-w-[25em] min-w-[25em] flex-col font-['GT-Walsheim-Pro-Light'] text-[1.6rem] leading-[1.6] max-[375px]:max-w-full max-[375px]:min-w-0">
              {instructions}
            </p>
            <Formik initialValues={initialValue} onSubmit={handleConfirm} validationSchema={validationSchema}>
              <Form className='flex w-full flex-col gap-[2em] [&>div:first-child]:mr-0'>
                <TextInput name='confirmTest' type='text' placeholder={confirmTestString} />
                <div className='flex justify-center'>
                  <PrimaryButton type='submit' small alert {...buttonProps}>
                    {title}
                  </PrimaryButton>
                </div>
              </Form>
            </Formik>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ConfirmIrreversible;
